import React, { useState, useEffect, useRef, useCallback } from 'react';
import LocationInput from '../location/LocationInput';
import FloorConfiguration from './FloorConfiguration';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera as faCameraRegular } from '@fortawesome/free-regular-svg-icons';
import {
  faFileDownload,
  faTrash,
  faEdit,
  faCheck,
  faTimes,
  faPlus,
  faClose,
} from '@fortawesome/free-solid-svg-icons';
import { BrowserMultiFormatReader } from '@zxing/library';
import { resolveApiUrl, API_ROOT } from '../../config';
import { formatDateToIso } from '../../DemandPointContext';
import PhotoViewer from '../common/PhotoViewer';
import {FLOOR_TYPES} from "../../constants/floorTypes";
import {BUILDING_TYPE} from "./type/BuildingType";

// Guess MIME from filename
const guessMimeFromName = (name = '') => {
  const n = String(name).toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.gif')) return 'image/gif';
  return 'image/jpeg';
};

// Convert base64 (no prefix) to Blob
const base64ToBlob = (b64, mime) => {
  const byteChars = atob(b64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mime || 'application/octet-stream' });
};

// Normalize photos coming from backend so UI can render them
const normalizeBuildingPhotos = (photos) => {
  const seen = new Set();
  return (photos || [])
    .map((p) => {
      // Backend sends raw base64 in p.data. Create a Blob URL for rendering.
      let objectUrl;
      const raw = p?.data;
      if (typeof raw === 'string' && raw.length && !raw.startsWith('data:') && !raw.startsWith('http')) {
        const filename =
          p.editable_filename || p.structured_filename || p.stored_filename || 'photo.jpg';
        const mime = p.mime_type || guessMimeFromName(filename);
        try {
          const blob = base64ToBlob(raw, mime);
          objectUrl = URL.createObjectURL(blob);
        } catch {
          objectUrl = undefined;
        }
      }

      const url = p.url
        ? resolveApiUrl(p.url)
        : p.file_path
          ? resolveApiUrl('/' + String(p.file_path).replace(/^\//, ''))
          : undefined;

      return {
        id: p.id || p.photo_id || crypto.randomUUID(),
        category: p.category || p.photo_category || p.photo_type || 'Sonstige',
        data: p.data || null,           // keep raw base64 as-is
        objectUrl,                      // Blob URL for <img src>
        url,                            // fallback server URL if available
        structured_filename: p.structured_filename,
        editable_filename: p.editable_filename,
        created_at: p.created_at,
      };
    })
    .filter((p) => {
      const key = p.id || p.structured_filename || p.objectUrl || p.url || p.data;
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const EMPTY_IMG =
  'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='; // prevents alt text overlay

const getDocumentUrl = (objectName, loadThumbnail = false) => {
  return `${API_ROOT}/document?documentType=BUILDING&objectName=${encodeURIComponent(objectName)}&loadThumbnail=${loadThumbnail}`;
};

const photoSrc = (document) => {
  if (!document) return '';
  if (document.objectUrl) return document.objectUrl;
  if (typeof document.data === 'string' && document.data.startsWith('data:')) return document.data;
  return getDocumentUrl(document.externalDocumentId, true);
};
const BuildingForm = ({ onSubmit, building = null, onDraftSave = null, onCancel = null }) => {
  // Calculate total units from floor-specific data (correct way) with fallback

  const calculateTotalUnitsFromFloors = (floors, fallbackBuilding = null) => {
    if (!floors || floors.length === 0) {
      if (fallbackBuilding) {
        return {
          residential: fallbackBuilding.residential_units || 0,
          business: fallbackBuilding.business_units || 0,
        };
      }
      return { residential: 0, business: 0 };
    }

    const actualFloors = floors.filter((floor) => floor.floorType !== 'unbekannt');
    const hasFloorUnitData = actualFloors.some(
      (floor) =>
        (floor.residential_units !== undefined && floor.residential_units !== null) ||
        (floor.business_units !== undefined && floor.business_units !== null)
    );

    if (hasFloorUnitData) {
      const totalResidential = actualFloors.reduce(
        (sum, floor) => sum + (parseInt(floor.residential_units) || 0),
        0
      );
      const totalBusiness = actualFloors.reduce(
        (sum, floor) => sum + (parseInt(floor.business_units) || 0),
        0
      );
      return { residential: totalResidential, business: totalBusiness };
    } else {
      if (fallbackBuilding) {
        return {
          residential: fallbackBuilding.residential_units || 0,
          business: fallbackBuilding.business_units || 0,
        };
      }
      return { residential: 0, business: 0 };
    }
  };

  const unitTotals = calculateTotalUnitsFromFloors(building?.floors, building);

  const getFloorDisplayName = (floorType) => {
    return FLOOR_TYPES[floorType];
  };

  const [formData, setFormData] = useState({
    externalId: building?.externalId,
    demand_point_id: building?.demand_point_id || null,
    address: {
      street: building?.address?.street || '',
      house_number: building?.address?.house_number ?? '',
      house_number_suffix: building?.address?.house_number_suffix || '',
      zip_code: building?.address?.zip_code || '',
      city: building?.address?.city || '',
      district: building?.address?.district || '',
      federal_state: building?.address?.federal_state || '',
      country: building?.address?.country || 'Deutschland',
      address_id: building?.address?.address_id || null,
    },
    location: {
      id: building?.location?.id || crypto.randomUUID(),
      latitude: building?.location?.latitude || 0,
      longitude: building?.location?.longitude || 0,
      accuracy: building?.location?.accuracy || 0,
      timestamp: building?.location?.timestamp || new Date().toISOString(),
    },
    total_floors: building?.total_floors || 1,
    residential_units: unitTotals.residential,
    business_units: unitTotals.business,
    name: building?.name || '',
    type: building?.type,
    access_notes: building?.access_notes || '',
    floors: (building?.floors || []).map((floor, index) => ({
      ...floor,
      floorType: floor.floorType || FLOOR_TYPES.UNKNOWN.value,
      floorNumber: floor.floorNumber || index + 1,
      display_name: floor.display_name || FLOOR_TYPES[floor.floorType || FLOOR_TYPES.UNKNOWN.value].label,
      units: floor.units || [],
    })),
    documents: (building?.documents || []).map((doc) => ({
      ...doc,
      tempId: doc.tempId || crypto.randomUUID()
    })),
  });
  const [houseNumberError, setHouseNumberError] = useState('');
  const [locationError, setLocationError] = useState('');

  const [buildingContentTab, setBuildingContentTab] = useState(0);

  const [isBuildingDrawing, setIsBuildingDrawing] = useState(false);
  // Photo Viewer States
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState([]);

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [buildingDocumentUploadModal, setBuildingDocumentUploadModal] = useState(false);

  const [currentBuildingDocumentType, setCurrentBuildingDocumentType] = useState(''); // TO REMOME !!!

  const [documentPreviews, setDocumentPreviews] = useState({});
  const [editingDocId, setEditingDocId] = useState(null);

  const [tempDisplayName, setTempDisplayName] = useState('');

  const fetchDocumentPreview = useCallback(async (externalDocumentId) => {
    if (documentPreviews[externalDocumentId]) return;
    try {
      const url = getDocumentUrl(externalDocumentId, true);
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setDocumentPreviews(prev => ({ ...prev, [externalDocumentId]: blobUrl }));
      }
    } catch (err) {
      console.error('Error fetching document preview:', err);
    }
  }, [documentPreviews]);

  const downloadDocument = async (objectName, displayName) => {
    try {
      const url = getDocumentUrl(objectName, false);
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = displayName || objectName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Fehler beim Herunterladen des Dokuments');
    }
  };

  const handleApiDocumentUpload = async (event, buildingDocumentType) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    // Basic client-side checks
    if (file.size > 5 * 1024 * 1024) {
      alert('Datei ist zu groß (max. 5 MB)');
      if (event.target) event.target.value = '';
      return;
    }

    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (allowed.indexOf(file.type) === -1) {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        alert('Nur PDF, JPG, PNG und Word Dateien sind erlaubt.');
        if (event.target) event.target.value = '';
        return;
      }
    }

    // Build FormData and POST to API
    const formDataUpload = new FormData();
    formDataUpload.append('documentType', 'BUILDING');
    formDataUpload.append('displayName', file.name);
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(`${API_ROOT}/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const newDoc = await response.json();

      const normalizedDoc = {
        tempId: newDoc.id || crypto.randomUUID(),
        externalDocumentId: newDoc.externalDocumentId || newDoc.externalId,
        displayName: newDoc.displayName || file.name,
        fileSize: newDoc.fileSize || file.size,
        documentType: 'BUILDING',
        dateOfCreation: newDoc.dateOfCreation ? formatDateToIso(newDoc.dateOfCreation) : new Date().toISOString(),
        lastModificationDate: newDoc.lastModificationDate ? formatDateToIso(newDoc.lastModificationDate) : new Date().toISOString(),
        version: newDoc.version,
        tags: newDoc.tags || [],
        buildingDocumentType: buildingDocumentType,
      };

      setFormData((current) => ({
        ...current,
        documents: [...(current.documents || []), normalizedDoc],
      }));

      // create local preview for images
      if (file.type.startsWith('image/')) {
        const localUrl = URL.createObjectURL(file);
        setDocumentPreviews((prev) => ({ ...prev, [normalizedDoc.externalDocumentId]: localUrl }));
      }

      // close modal and reset
      setBuildingDocumentUploadModal(false);
      setCurrentBuildingDocumentType('');

    } catch (err) {
      console.error('Error uploading building document:', err);
      alert('Fehler beim Hochladen des Dokuments');
    } finally {
      try { if (event.target) event.target.value = ''; } catch (e) {}
    }
  };

  const handleApiDocumentDelete = (externalDocumentId) => {
    if (window.confirm('Wollen Sie dieses Dokument wirklich löschen?')) {
      setFormData(prev => ({
        ...prev,
        documents: (prev.documents || []).filter(doc => doc.externalDocumentId !== externalDocumentId)
      }));
    }
  };

  const handleApiDocumentRenameStart = (doc) => {
    setEditingDocId(doc.externalDocumentId);
    setTempDisplayName(doc.displayName);
  };

  const handleApiDocumentRenameSave = (externalDocumentId) => {
    if (!tempDisplayName.trim()) return;
    setFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).map(doc =>
        doc.externalDocumentId === externalDocumentId
          ? { ...doc, displayName: tempDisplayName.trim() }
          : doc
      )
    }));
    setEditingDocId(null);
    setTempDisplayName('');
  };

  const handleApiDocumentRenameCancel = () => {
    setEditingDocId(null);
    setTempDisplayName('');
  };

  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const apiDocumentInputRef = useRef(null);

  useEffect(() => {
    if (building) {
      let floorsWithUnits = building?.floors?.filter((floor) => floor.floorType !== FLOOR_TYPES.UNKNOWN.value) || [];

      const editUnitTotals = {
        residential: building?.residential_units || 0,
        business: building?.business_units || 0,
      };

      setFormData({
        externalId: building?.externalId,
        demand_point_id: building?.demand_point_id || null,
        address: {
          street: building?.address?.street || '',
          house_number: building?.address?.house_number ?? '',
          house_number_suffix: building?.address?.house_number_suffix || '',
          zip_code: building?.address?.zip_code || '',
          city: building?.address?.city || '',
          district: building?.address?.district || '',
          federal_state: building?.address?.federal_state || '',
          country: building?.address?.country || 'Deutschland',
          address_id: building?.address?.address_id || null,
        },
        location: {
          id: building?.location?.id || crypto.randomUUID(),
          latitude: building?.location?.latitude || 0,
          longitude: building?.location?.longitude || 0,
          accuracy: building?.location?.accuracy || 0,
          timestamp: building?.location?.timestamp || new Date().toISOString(),
        },
        total_floors: building?.total_floors || 1,
        residential_units: editUnitTotals.residential,
        business_units: editUnitTotals.business,
        name: building?.name || '',
        type: building?.type,
        access_notes: building?.access_notes || '',
        floors: floorsWithUnits,
        photos: normalizeBuildingPhotos(building?.photos),
        // NEW: populate documents from incoming building (if present)
        documents: (building?.documents || []).map((doc) => ({
          ...doc,
          tempId: doc.tempId || crypto.randomUUID()
        })),
      });

      // NEW: also hydrate the per-type UI lists so they are visible on "Bearbeiten"
      const grouped = Object.keys(DOCUMENT_CARD_BUILDING_DOCUMENT_TYPES).reduce((acc, k) => {
        acc[k] = [];
        return acc;
      }, {});
      (building?.documents || []).forEach((doc) => {
        const key = doc.docType || 'unknown';
        const item = {
          id: doc.id || crypto.randomUUID(),
          // display original filename from DB
          fileName: doc.originalFilename || doc.storedFilename || 'Dokument',
          fileSize: doc.fileSize || 0,
          mimeType: doc.mimeType || 'application/octet-stream',
          // optional preview/download if backend provides url/data
          url: doc.url ? resolveApiUrl(doc.url) : undefined,
          data: doc.data,
          uploadDate: formatDateToIso(doc.dateOfCreation || new Date()),
        };
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      });
    }
  }, [building]);

  useEffect(() => {
    codeReader.current = new BrowserMultiFormatReader();
    return () => {
      if (codeReader.current) {
        codeReader.current.reset();
      }
    };
  }, []);

  useEffect(() => {
    codeReader.current = new BrowserMultiFormatReader();
    return () => {
      if (codeReader.current) {
        codeReader.current.reset();
      }
    };
  }, []);

  useEffect(() => {
    if (formData.documents && formData.documents.length > 0) {
      formData.documents.forEach(doc => {
        if (doc.externalDocumentId && !documentPreviews[doc.externalDocumentId]) {
          fetchDocumentPreview(doc.externalDocumentId);
        }
      });
    }
  }, [formData.documents, fetchDocumentPreview, documentPreviews]);

  useEffect(() => {
    // Cleanup previous object URLs
    return () => {
      Object.values(documentPreviews).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    // Cleanup previous object URLs
    return () => {
      (formData.photos || []).forEach((p) => {
        if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
      });
    };
  }, [formData.photos]);

  const validateHouseNumber = (houseNumber) => {
    const houseNumberRegex = /^\d+$/;
    return houseNumberRegex.test(houseNumber.trim());
  };

  const handleHouseNumberBlur = (e) => {
    const value = e.target.value;
    if (value && !validateHouseNumber(value)) {
      setHouseNumberError(
        'Hausnummer darf nur Zahlen enthalten. Bereiche wie "16-20" bitte als Hausnummer "16" und Zusatz "20" erfassen.'
      );
    } else {
      setHouseNumberError('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newFormData;
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        newFormData = {
          ...formData,
          [parent]: {
            ...formData[parent],
            [child]: value,
          },
        };
      } else if (parts.length === 3) {
        const [parent, middle, child] = parts;
        newFormData = {
          ...formData,
          [parent]: {
            ...formData[parent],
            [middle]: {
              ...formData[parent][middle],
              [child]: value,
            },
          },
        };
      }
    } else {
      newFormData = {
        ...formData,
        [name]:
          name === 'total_floors' || name === 'residential_units' || name === 'business_units'
            ? parseInt(value) || 0
            : value,
      };
    }

    setFormData(newFormData);
    if (!building && onDraftSave) {
      onDraftSave(newFormData);
    }
  };

  const handleLocationSelect = (location) => {
    setFormData((prev) => ({
      ...prev,
      location: {
        ...location,
        timestamp: new Date().toISOString(),
      },
    }));
    // Clear validation error when coordinates are selected
    if (location?.latitude && location?.longitude) {
      setLocationError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous errors
    setLocationError('');

    // Validate coordinates presence
    if (!formData.location || !formData.location.latitude || !formData.location.longitude) {
      setLocationError('GPS-Position ist erforderlich. Bitte verwenden Sie den "Aktuellen Standort verwenden" Button.');
      alert('Bitte geben Sie eine GPS-Position ein');
      return;
    }

    // Validate coordinates validity
    const lat = Number(formData.location.latitude);
    const lng = Number(formData.location.longitude);

    if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
      setLocationError('Ungültige GPS-Koordinaten. Bitte verwenden Sie den "Aktuellen Standort verwenden" Button.');
      alert('Ungültige GPS-Position');
      return;
    }

    // Submit now includes formData.documents (array) in the payload, like photos
    onSubmit(formData);
  };

  const [photoStream, setPhotoStream] = useState(null);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [currentPhotoCategory, setCurrentPhotoCategory] = useState('');
  const photoVideoRef = useRef(null);

  const startPhotoCapture = async (category) => {
    try {
      const totalPhotos = (formData.photos || []).length;
      if (totalPhotos >= 6) {
        alert('Maximale Anzahl von 6 Fotos erreicht');
        return;
      }

      const categoryPhotos = (formData.photos || []).filter((p) => p.category === category).length;
      if (categoryPhotos >= 2) {
        alert(`Maximale Anzahl von 2 Fotos für ${category} erreicht`);
        return;
      }

      setCurrentPhotoCategory(category);
      setShowPhotoCapture(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      setPhotoStream(stream);
      setTimeout(() => {
        if (photoVideoRef.current) {
          photoVideoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Kamera konnte nicht geöffnet werden: ' + error.message);
      setShowPhotoCapture(false);
    }
  };

  const capturePhoto = async () => {
    if (!photoVideoRef.current || !photoStream) return;
    const canvas = document.createElement('canvas');
    const video = photoVideoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    const sizeInBytes = Math.round((dataUrl.length - 22) * 0.75);
    const sizeInMB = sizeInBytes / (1024 * 1024);
    if (sizeInMB > 2) {
      alert('Foto ist zu groß (max. 2 MB). Bitte versuchen Sie es erneut.');
      return;
    }

    const token = localStorage.getItem('fastplanet_token');
    const activeBuildingId = building?.id || formData?.id;

    if (!activeBuildingId) {
      console.warn('No building ID available yet; storing photo locally only.');
    }

    const newPhoto = {
      category: currentPhotoCategory,
      data: dataUrl,
      timestamp: new Date().toISOString(),
      building_id: activeBuildingId,
    };

    setFormData((prev) => ({
      ...prev,
      photos: [...(prev.photos || []), newPhoto],
    }));

    if (activeBuildingId && token) {
      // POST /api/photos does not exist on the backend.
      // Photos are stored locally and submitted as base64 with the building payload.
      console.log('Photo captured and stored locally. Will be submitted with building save.');
    }

    stopPhotoCapture();
  };

  const stopPhotoCapture = () => {
    if (photoStream) {
      photoStream.getTracks().forEach((track) => track.stop());
      setPhotoStream(null);
    }
    setShowPhotoCapture(false);
    setCurrentPhotoCategory('');
  };

  const removeDocument = (documentType, indexToRemove) => {
    let formDataDocuments = formData?.documents || [];
    const updatedDocuments = formDataDocuments.filter((doc, index) => {
      if (doc.buildingDocumentType !== documentType) return true;
      const categoryIndex = formDataDocuments.filter((p) => p.buildingDocumentType === documentType).indexOf(doc);
      return categoryIndex !== indexToRemove;
    });

    setFormData((prev) => ({
      ...prev,
      documents: updatedDocuments,
    }));
  };

  const startBuildingDocumentUpload = (docType) => {
    setCurrentBuildingDocumentType(docType);
    setBuildingDocumentUploadModal(true);
  };

  const removeBuildingDocument = (tempIdToRemove) => {
    // Remove from payload array as well
    setFormData((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter((d) => (d.tempId) !== tempIdToRemove),
    }));
  };

  const PHOTO_DOCUMENT_TYPES = [
    'PHOTO_EXTERIOR_VIEW',
    'PHOTO_KELLER',
    'PHOTO_STAIRCASE',
    'PHOTO_GV_OFFEN',
    'PHOTO_HOUSE_BREAKTHROUGH_WITH_GV',
    'PHOTO_OTHER',
  ];

  const DUCUMENT_TAB_OTER_BUILDING_DOCUMENT_TYPES = [
    'OTHER',
  ]

  const DOCUMENT_CARD_BUILDING_DOCUMENT_TYPES = {
    BUILDING_PERMIT: 'Bauakte (Materialbedarf)',
    FIBER_ALLOCATION_PLAN: 'Faserzuteilungsplan',
    RECONNAISSANCE_REPORT: 'Auskundungsprotokoll',
  };

  const PHOTO_DOCUMENT_MAP = {
    PHOTO_EXTERIOR_VIEW: 'Außenansicht',
    PHOTO_KELLER: 'Keller',
    PHOTO_STAIRCASE: 'Treppenhaus',
    PHOTO_GV_OFFEN: 'GV offen',
    PHOTO_HOUSE_BREAKTHROUGH_WITH_GV: 'Hausdurchbruch mit GV',
    PHOTO_OTHER: 'Sonstige',
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-gray-900">
          {building ? 'Gebäude bearbeiten' : 'Neues Gebäude erfassen'}
        </h3>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="form-label">Straße</label>
          <input disabled type="text" name="address.street" value={formData.address.street} onChange={handleInputChange} required className="form-input" />
        </div>
        <div>
          <label className="form-label">Hausnr.</label>
          <input
            disabled
            type="text"
            name="address.house_number"
            value={formData.address.house_number}
            onChange={handleInputChange}
            onBlur={handleHouseNumberBlur}
            required
            className={`form-input ${houseNumberError ? 'border-red-500' : ''}`}
            placeholder="16"
            title="Nur Zahlen. Bei Bereichen (z.B. 16-20): Hausnummer '16', Zusatz '20'"
          />
          {houseNumberError && <div className="text-red-500 text-xs mt-1">{houseNumberError}</div>}
        </div>
        <div>
          <label className="form-label">Zusatz</label>
          <input
            disabled
            type="text"
            name="address.house_number_suffix"
            value={formData.address.house_number_suffix}
            onChange={handleInputChange}
            className="form-input"
            placeholder="a, 20, A1009B"
            title="Buchstaben, Bereichsende oder Kombinationen (z.B. 'a', '20', 'A1009B')"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="form-label">PLZ</label>
          <input disabled type="text" name="address.zip_code" value={formData.address.zip_code} onChange={handleInputChange} required className="form-input" />
        </div>
        <div>
          <label className="form-label">Ort</label>
          <input disabled type="text" name="address.city" value={formData.address.city} onChange={handleInputChange} required className="form-input" />
        </div>
        <div>
          <label className="form-label">Ortsteil (optional)</label>
          <input disabled type="text" name="address.district" value={formData.address.district} onChange={handleInputChange} className="form-input" placeholder="Stadtteil/Bezirk" />
        </div>
      </div>

      <div>
        <label className="form-label">Bundesland</label>
        <input disabled type="text" name="address.federal_state" value={formData.address.federal_state} onChange={handleInputChange} required className="form-input" placeholder="z.B. Bayern" />
      </div>

      <div>
        <label className="form-label">
          GPS-Position <span style={{ color: 'red' }}>*</span>
        </label>
        <LocationInput onLocationSelect={handleLocationSelect} initialLocation={formData.location} disabled={!!building}/>
        {locationError && (
          <p style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {locationError}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">Wohneinheiten (berechnet)</label>
          <input type="number" name="residential_units" value={formData.residential_units} readOnly className="form-input bg-gray-100 cursor-not-allowed" title="Wird automatisch aus der Etagen-Konfiguration berechnet" />
        </div>
        <div>
          <label className="form-label">Geschäftseinheiten (berechnet)</label>
          <input type="number" name="business_units" value={formData.business_units} readOnly className="form-input bg-gray-100 cursor-not-allowed" title="Wird automatisch aus der Etagen-Konfiguration berechnet" />
        </div>
      </div>

      <FloorConfiguration
        selectedFloors={formData.floors}
        initialFlors={building?.floors || []}
        onFloorChange={(floors) => {
          const actualFloors = floors.filter((floor) => floor.floorType !== FLOOR_TYPES.UNKNOWN.value);
          const totalResidentialUnits = actualFloors.reduce((sum, floor) => sum + (parseInt(floor?.units?.livingUnitCount) || 0), 0);
          const totalBusinessUnits = actualFloors.reduce((sum, floor) => sum + (parseInt(floor?.units?.commercialUnitCount) || 0), 0);
          setFormData((prev) => ({
            ...prev,
            floors,
            total_floors: floors.length,
            residential_units: totalResidentialUnits,
            business_units: totalBusinessUnits,
          }));
        }}
        totalFloors={formData.total_floors}
      />

      <div>
        <label className="form-label">Name</label>
        <input
            name={"name"}
            type="text"
            placeholder="Name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full text-sm px-3 py-2 border rounded focus:outline-none focus:ring-1 border-gray-300 focus:ring-orange-500 focus:border-orange-500`}
        />
      </div>

      <div>
        <label className="form-label">Gebäudetyp</label>
        <select
            name="type"
            value={formData.type}
            onChange={handleInputChange}
            required
            className="form-select"
        >
          {Object.entries(BUILDING_TYPE).map(([key, entry]) => (
              <option key={key}
                      value={entry.value}
              >{entry.label}
              </option>))}
        </select>
      </div>

      <div className="space-y-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
             type="button"
              onClick={(e) => {
                e.preventDefault();
                setBuildingContentTab(0);
              }}
              onTouchStart={(e) => e.preventDefault()}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                buildingContentTab === 0
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Gebäude-Fotos
            </button>
            <button
             type="button"
              onClick={(e) => {
                e.preventDefault();
                setBuildingContentTab(1);
              }}
              onTouchStart={(e) => e.preventDefault()}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                buildingContentTab === 1
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dokumente
            </button>
          </nav>
        </div>

        <div className="mt-6">
          {buildingContentTab === 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-4">Gebäude-Fotos</h3>
              <div className="grid grid-cols-2 gap-4">
                {PHOTO_DOCUMENT_TYPES.map((documentType) => (
                  <div key={documentType} className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">{PHOTO_DOCUMENT_MAP[documentType]}</h4>
                    <div className="space-y-2">
                      {(() => {
                        const categoryPhotos = (formData.documents || []).filter(
                          (document) => (document.buildingDocumentType) === documentType
                        );
                        // show at most 2 per category
                        return categoryPhotos.slice(0, 2).map((document, index) => {
                          const displayName =
                            document.displayName ||
                            `${PHOTO_DOCUMENT_MAP[documentType]} ${index + 1}`;
                          return (
                            <div key={document.id || `${documentType}-${index}`} className="space-y-1">
                              <div className="relative">
                                <img
                                  src={documentPreviews[document?.externalDocumentId] || EMPTY_IMG}
                                  alt={`${documentType} ${index + 1}`}
                                  className="w-full h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                  onClick={() => {
                                    // Get all photos for PhotoViewer
                                    const allPhotos = (formData.photos || []);
                                    const photoIndex = allPhotos.findIndex(p => p.id === document.id);
                                    setViewerPhotos(allPhotos);
                                    setCurrentPhotoIndex(photoIndex >= 0 ? photoIndex : 0);
                                    setPhotoViewerOpen(true);
                                  }}
                                  onError={(e) => { e.currentTarget.src = EMPTY_IMG; }}
                                />
                                <div className="absolute top-1 right-1 flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => downloadDocument(document.externalDocumentId, document.displayName)}
                                    className="w-6 h-6 leading-6 text-center rounded-full bg-orange-500 text-white text-xs hover:bg-red-600"
                                    title="Foto bearbeiten"
                                  >
                                    <div className="flex flex-col items-center justify-center text-white-400">
                                      <FontAwesomeIcon icon={faFileDownload} size="1x" />
                                    </div>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeDocument(documentType, index)}
                                    className="w-6 h-6 leading-6 text-center rounded-full bg-orange-500 text-white text-xs hover:bg-red-600"
                                    title="Foto löschen"
                                  >
                                    <div className="flex flex-col items-center justify-center text-white-400">
                                      <FontAwesomeIcon icon={faClose} size="1x" />
                                    </div>
                                  </button>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 truncate" title={displayName}>
                                {displayName}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {((formData.documents || []).filter(
                      (doc) => (doc.buildingDocumentType) === documentType
                    ).length) < 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          const fileInput = document.createElement('input');
                          fileInput.type = 'file';
                          fileInput.accept = 'image/*';
                          fileInput.capture = 'environment';
                          fileInput.style.display = 'none';
                          document.body.appendChild(fileInput);
                          fileInput.addEventListener('change', async function (event) {
                            const file = event.target.files[0];
                            if (!file) {
                              document.body.removeChild(fileInput);
                              return;
                            }

                            // Basic client-side checks
                            if (file.size > 5 * 1024 * 1024) {
                              alert('Datei ist zu groß (max. 5 MB)');
                              document.body.removeChild(fileInput);
                              return;
                            }

                            const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
                            if (allowed.indexOf(file.type) === -1) {
                              // allow images even without exact mime
                              if (!file.type.startsWith('image/')) {
                                alert('Nur PDF, JPG und PNG Dateien sind erlaubt.');
                                document.body.removeChild(fileInput);
                                return;
                              }
                            }

                            // Build FormData and POST to API
                            const formDataUpload = new FormData();
                            formDataUpload.append('documentType', 'BUILDING');
                            formDataUpload.append('displayName', file.name);
                            formDataUpload.append('file', file);

                            try {
                              const token = localStorage.getItem('fastplanet_token');
                              const response = await fetch(`${API_ROOT}/document`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                },
                                body: formDataUpload,
                              });

                              if (!response.ok) {
                                throw new Error('Upload failed');
                              }

                              const newDoc = await response.json();

                              const normalizedDoc = {
                                tempId: newDoc.tempId || crypto.randomUUID(),
                                externalDocumentId: newDoc.externalDocumentId || newDoc.externalId,
                                displayName: newDoc.displayName || file.name,
                                fileSize: newDoc.fileSize || file.size,
                                documentType: newDoc.documentType || 'BUILDING',
                                dateOfCreation: newDoc.dateOfCreation ? formatDateToIso(newDoc.dateOfCreation) : new Date().toISOString(),
                                lastModificationDate: newDoc.lastModificationDate ? formatDateToIso(newDoc.lastModificationDate) : new Date().toISOString(),
                                version: newDoc.version,
                                tags: newDoc.tags || [],
                                buildingDocumentType: documentType,
                              };

                              // add to form payload
                              setFormData((current) => ({
                                ...current,
                                documents: [...(current.documents || []), normalizedDoc],
                              }));

                              // create local preview for images
                              if (file.type.startsWith('image/')) {
                                const localUrl = URL.createObjectURL(file);
                                setDocumentPreviews((prev) => ({ ...prev, [normalizedDoc.externalDocumentId]: localUrl }));
                              }
                            } catch (err) {
                              console.error('Error uploading document from device:', err);
                              alert('Fehler beim Hochladen des Dokuments');
                            } finally {
                              try { document.body.removeChild(fileInput); } catch (e) {}
                            }
                          });
                          fileInput.click();
                        }}
                        className="w-full mt-2 p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2"
                      >
                        <FontAwesomeIcon icon={faCameraRegular} className="text-gray-500" />
                        <span className="text-sm text-gray-600">Device-Kamera</span>
                      </button>
                    )}

                    <div className="text-xs text-gray-500 mt-1 text-center">
                      {((formData.documents || []).filter(
                        (doc) => (doc.buildingDocumentType) === documentType
                      ).length)}/2 Fotos
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-sm text-gray-600 text-center">Gesamt: {(formData?.documents.filter(doc => PHOTO_DOCUMENT_TYPES.includes(doc?.buildingDocumentType)) || []).length}/14 Fotos</div>
            </div>
          )}

          {buildingContentTab === 1 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-700">Gebäude-Dokumente</h3>
              </div>

              {/* API Documents Section */}
              <div className="mb-8 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-semibold text-orange-800 uppercase tracking-wider">Gebäude-Dokumente</h4>
                  <button
                    type="button"
                    onClick={() => apiDocumentInputRef.current.click()}
                    className="flex items-center space-x-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors text-xs font-medium"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Dokument hochladen</span>
                  </button>
                  <input
                    type="file"
                    ref={apiDocumentInputRef}
                    onChange={event => handleApiDocumentUpload(event, "OTHER")}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                </div>

                {formData.documents && formData.documents.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {formData.documents.filter(doc => DUCUMENT_TAB_OTER_BUILDING_DOCUMENT_TYPES.includes(doc?.buildingDocumentType)).map((doc) => (
                      <div key={doc.externalDocumentId} className="bg-white p-3 rounded shadow-sm border border-orange-100 flex flex-col">
                        <div className="aspect-square mb-2 bg-gray-100 rounded overflow-hidden flex items-center justify-center relative group">
                          <img
                            src={documentPreviews[doc.externalDocumentId]}
                            alt={doc.displayName}
                            className="w-full h-full object-contain"
                            style={{ display: documentPreviews[doc.externalDocumentId] ? 'block' : 'none' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          {!documentPreviews[doc.externalDocumentId] && (
                            <div className="flex flex-col items-center justify-center text-gray-400">
                              <FontAwesomeIcon icon={faFileDownload} size="2x" />
                              <span className="text-[10px] mt-1">Keine Vorschau</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center space-x-2">
                            <button
                              type="button"
                              onClick={() => downloadDocument(doc.externalDocumentId, doc.displayName)}
                              className="opacity-0 group-hover:opacity-100 p-2 bg-white rounded-full shadow-lg text-orange-600 hover:text-orange-700 transition-opacity"
                              title="Herunterladen"
                            >
                              <FontAwesomeIcon icon={faFileDownload} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleApiDocumentDelete(doc.externalDocumentId)}
                              className="opacity-0 group-hover:opacity-100 p-2 bg-white rounded-full shadow-lg text-red-600 hover:text-red-700 transition-opacity"
                              title="Löschen"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>

                        {editingDocId === doc.externalDocumentId ? (
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={tempDisplayName}
                              onChange={(e) => setTempDisplayName(e.target.value)}
                              className="text-[11px] w-full border rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-orange-500"
                              autoFocus
                            />
                            <div className="flex justify-end space-x-1">
                              <button
                                type="button"
                                onClick={() => handleApiDocumentRenameSave(doc.externalDocumentId)}
                                className="text-green-600 hover:text-green-700 p-0.5"
                              >
                                <FontAwesomeIcon icon={faCheck} size="sm" />
                              </button>
                              <button
                                type="button"
                                onClick={handleApiDocumentRenameCancel}
                                className="text-gray-600 hover:text-gray-700 p-0.5"
                              >
                                <FontAwesomeIcon icon={faTimes} size="sm" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between group/name">
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] font-medium text-gray-800 truncate" title={doc.displayName}>
                                {doc.displayName}
                              </div>
                              <div className="text-[10px] text-gray-500">
                                {(doc.fileSize / 1024).toFixed(1)} KB
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleApiDocumentRenameStart(doc)}
                              className="opacity-0 group-hover/name:opacity-100 text-gray-400 hover:text-orange-600 p-1 transition-opacity"
                              title="Umbenennen"
                            >
                              <FontAwesomeIcon icon={faEdit} size="xs" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-orange-100 rounded-lg bg-orange-50/50">
                    Keine Dokumente vorhanden
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(DOCUMENT_CARD_BUILDING_DOCUMENT_TYPES).map(([buildDocType, label]) => {
                  let docs = formData?.documents.filter(doc => doc.buildingDocumentType === buildDocType) || [] ;
                  return (
                    <div key={buildDocType} className="border rounded-lg p-4 bg-white">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">{label}</h4>
                      <div className="space-y-2">
                        {docs.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                            <span className="truncate flex-1" title={doc.displayName}>
                              {doc.displayName}
                            </span>
                            <button type="button" onClick={() => downloadDocument(doc.externalDocumentId, doc.displayName)} className="ml-2 text-red-500 hover:text-red-700" title="Dokument löschen">
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <FontAwesomeIcon icon={faFileDownload} className={"text-orange-600"} />
                              </div>
                            </button>
                            <button type="button" onClick={() => removeBuildingDocument(doc.tempId)} className="ml-2 text-red-500 hover:text-red-700" title="Dokument löschen">
                              ×
                            </button>
                          </div>
                        ))}
                        <button type="button" onClick={() => startBuildingDocumentUpload(buildDocType)} className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors text-sm">
                          + Dokument hinzufügen
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 mt-2 text-center">{docs.length} Dokument{docs.length !== 1 ? 'e' : ''}</div>
                    </div>
                  );
                })}
              </div>

              {formData?.documents && formData.documents.filter(doc => !PHOTO_DOCUMENT_TYPES.includes(doc?.buildingDocumentType)).length === 0 && (
                <div className="text-center py-8 text-gray-500 mt-6">
                  <div className="text-gray-400 mb-3">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-sm">Noch keine Gebäude-Dokumente vorhanden</p>
                  <p className="text-xs mt-1">Verwenden Sie die Upload-Buttons um Dokumente hinzuzufügen</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="form-label">Zugangshinweise</label>
        <textarea name="access_notes" value={formData.access_notes} onChange={handleInputChange} rows="3" className="form-textarea" placeholder="Spezielle Zugangshinweise, Schlüssel, Ansprechpartner, etc." />
      </div>

      {showPhotoCapture && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Foto aufnehmen: {currentPhotoCategory}</h3>
              <button type="button" onClick={stopPhotoCapture} className="text-gray-500 hover:text-gray-700 text-xl">
                ×
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Positionieren Sie das Gebäude im Kamerasucher</p>
              <div className="relative bg-black rounded">
                <video ref={photoVideoRef} autoPlay playsInline muted className="w-full h-64 object-cover rounded" />
              </div>
              <div className="flex space-x-3">
                <button type="button" onClick={capturePhoto} className="flex-1 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600">
                  <FontAwesomeIcon icon={faCameraRegular} className="mr-2" />
                  Foto aufnehmen
                </button>
                <button type="button" onClick={stopPhotoCapture} className="flex-1 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
                  Abbrechen
                </button>
              </div>
              <div className="text-xs text-gray-500 text-center">Max. 2 MB pro Foto</div>
            </div>
          </div>
        </div>
      )}

      {buildingDocumentUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Dokument hochladen: {DOCUMENT_CARD_BUILDING_DOCUMENT_TYPES[currentBuildingDocumentType]}</h3>
              <button type="button" onClick={() => setBuildingDocumentUploadModal(false)} className="text-gray-500 hover:text-gray-700 text-xl">
                ×
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">Wählen Sie eine Datei aus (max. 5 MB)</p>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={event => handleApiDocumentUpload(event, currentBuildingDocumentType)} className="w-full p-2 border border-gray-300 rounded" />
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => setBuildingDocumentUploadModal(false)} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex space-x-4">
        <button type="button" onClick={onCancel} className="flex-1 btn-cancel px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors">
          {building ? 'Bearbeitung abbrechen' : 'Abbrechen'}
        </button>
        <button type="submit" className="flex-1 btn-primary">
          {building ? 'Gebäude aktualisieren' : 'Gebäude erfassen'}
        </button>
      </div>

      {/* Photo Viewer Modal */}
      {photoViewerOpen && (
        <PhotoViewer 
          photos={viewerPhotos}
          currentIndex={currentPhotoIndex}
          onClose={() => setPhotoViewerOpen(false)}
          onNavigate={(index) => setCurrentPhotoIndex(index)}
        />
      )}
    </form>
  );
};

export default BuildingForm;
